import { addAlertThunk } from 'actions/alerts/AlertsActions';
import { lookupCustomerThunk, saveCustomerThunk } from 'actions/customer/CustomerActions';
import {
    INTAKE_FORM_ADD_PET,
    INTAKE_FORM_ERROR,
    INTAKE_FORM_SET_CUSTOMER,
    INTAKE_FORM_SET_LOADING,
    INTAKE_FORM_SET_STEP,
    INTAKE_FORM_UPDATE_PET,
    IntakeFormAction
} from 'actions/intakeForm/IntakeFormActionsTypes';
import { fetchMarketplaceBySlugThunk } from 'actions/marketplace/MarketplaceActions';
import { fetchMasterDataThunk } from 'actions/masterData/MasterDataActions';
import { push } from 'connected-react-router';
import { IntakeFormStep } from 'reducers/intakeForm/IntakeFormState';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { RootAction } from 'types/Actions';
import { AlertScreen } from 'types/Alert';
import { Customer } from 'types/Customer';
import { Pet } from 'types/Pet';

const errorHandler = (error: unknown): IntakeFormAction => {
    console.error(error);

    return {
        type: INTAKE_FORM_ERROR
    };
};

const setLoading = (loading: boolean): IntakeFormAction => {
    return {
        type: INTAKE_FORM_SET_LOADING,
        payload: {
            loading
        }
    };
};

const setStep = (step: IntakeFormStep): IntakeFormAction => {
    return {
        type: INTAKE_FORM_SET_STEP,
        payload: { step }
    };
};

const setCustomer = (customer: Customer): IntakeFormAction => {
    return {
        type: INTAKE_FORM_SET_CUSTOMER,
        payload: { customer }
    };
};

const addPet = (pet: Pet): IntakeFormAction => {
    return {
        type: INTAKE_FORM_ADD_PET,
        payload: { pet }
    };
};

const updatePet = (pet: Pet): IntakeFormAction => {
    return {
        type: INTAKE_FORM_UPDATE_PET,
        payload: { pet }
    };
};

export const initIntakeFormThunk = (
    marketplaceSlug: string
): ThunkAction<void, RootState, null, RootAction> => {
    return async dispatch => {
        try {
            dispatch(setLoading(true));

            const marketplace = await dispatch(fetchMarketplaceBySlugThunk(marketplaceSlug));

            await dispatch(fetchMasterDataThunk(marketplace.id));

            dispatch(setStep(IntakeFormStep.CustomerForm));
            dispatch(setLoading(false));
        } catch (error) {
            dispatch(errorHandler(error));

            // Redirect to 404 page
            dispatch(push('/'));
        }
    };
};

export const intakeFormBackThunk = (): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        const currentStep = getState().intakeForm.step;

        switch (currentStep) {
            case IntakeFormStep.AddPetForm:
                dispatch(setStep(IntakeFormStep.CustomerForm));
                break;
            case IntakeFormStep.PetList:
                dispatch(setStep(IntakeFormStep.AddPetForm));
                break;
            default:
                break;
        }
    };
};

export const submitIntakeFormCustomerThunk = (
    customer: Customer
): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        try {
            dispatch(setLoading(true));

            const {
                marketplace,
                intakeForm: { pets }
            } = getState();
            const marketplaceId = marketplace.data.id;

            const lookupCustomer = await dispatch(
                lookupCustomerThunk(customer.email, marketplaceId)
            );

            if (lookupCustomer) {
                dispatch(
                    addAlertThunk(
                        'Oops! It looks like this email and/or phone number is already registered. Please reach out to us at your convenience so we can help.',
                        AlertScreen.IntakeFormCustomer
                    )
                );

                return;
            }

            dispatch(setCustomer(customer));

            if (pets.length > 0) {
                dispatch(setStep(IntakeFormStep.PetList));
            } else {
                dispatch(setStep(IntakeFormStep.AddPetForm));
            }
        } catch (error) {
            dispatch(errorHandler(error));
        } finally {
            dispatch(setLoading(false));
        }
    };
};

export const submitIntakeFormPetThunk = (
    pet: Pet
): ThunkAction<void, RootState, null, RootAction> => {
    return async dispatch => {
        try {
            dispatch(addPet(pet));

            dispatch(setStep(IntakeFormStep.PetList));
        } catch (error) {
            dispatch(errorHandler(error));
        }
    };
};

export const updateIntakeFormPetThunk = (
    pet: Pet
): ThunkAction<void, RootState, null, RootAction> => {
    return async dispatch => {
        try {
            dispatch(updatePet(pet));
        } catch (error) {
            dispatch(errorHandler(error));
        }
    };
};

export const submitIntakeFormThunk = (): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        try {
            dispatch(setLoading(true));

            const {
                intakeForm: { customer: intakeFormCustomer, pets }
            } = getState();

            await dispatch(
                saveCustomerThunk(
                    {
                        ...intakeFormCustomer,
                        pets
                    },
                    'intake-form'
                )
            );

            dispatch(setStep(IntakeFormStep.Success));
        } catch (error) {
            dispatch(errorHandler(error));

            dispatch(setStep(IntakeFormStep.CustomerForm));
        } finally {
            dispatch(setLoading(false));
        }
    };
};
